import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, Globe, EyeOff, Download, Upload, ExternalLink,
  FileText, Image as ImageIcon, Film, Package, Newspaper,
} from "lucide-react";
import { Modal, TextField, TextArea, SelectField, NumField, Toggle } from "./SponsorsPage";

const CATEGORIES = [
  { value: "press_release", label: "Press Release", icon: Newspaper },
  { value: "brand_asset",   label: "Brand Asset",   icon: FileText },
  { value: "photo",         label: "Photo",         icon: ImageIcon },
  { value: "video",         label: "Video",         icon: Film },
  { value: "logo_pack",     label: "Logo Pack",     icon: Package },
  { value: "other",         label: "Other",         icon: FileText },
];

const BLANK = {
  title: "", category: "press_release", description: "",
  external_url: "", published: false, sort_order: 0,
};

function formatSize(bytes) {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function MediaRoomPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const r = await api.get("/admin/media", { withCredentials: true });
    setItems(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (m) => {
    try {
      await api.patch(`/admin/media/${m.id}`, {
        title: m.title, category: m.category, description: m.description,
        external_url: m.external_url, published: !m.published, sort_order: m.sort_order || 0,
      }, { withCredentials: true });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media asset?")) return;
    try {
      await api.delete(`/admin/media/${id}`, { withCredentials: true });
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;
  const catIcon  = (v) => CATEGORIES.find((c) => c.value === v)?.icon || FileText;

  const filtered = filter === "all" ? items : items.filter((m) => m.category === filter);
  const totalDownloads = items.reduce((s, m) => s + (m.download_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">MEDIA ROOM</p>
          <h1 className="font-display text-4xl text-white">Press Releases &amp; Brand Assets</h1>
          <p className="text-white/50 mt-2 text-sm">
            Published assets appear in the public Media Room. Downloads are tracked automatically.
          </p>
        </div>
        {editable && (
          <button data-testid="add-media-btn" onClick={() => setEditing({ ...BLANK, _file: null })} className="btn-pink">
            <Plus size={16} /> NEW MEDIA ASSET
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Total Assets"     value={items.length} />
        <KPI label="Published"        value={items.filter((m) => m.published).length} />
        <KPI label="Total Downloads"  value={totalDownloads} />
        <KPI label="Categories"       value={new Set(items.map((m) => m.category)).size} />
      </div>

      {/* Filter pills */}
      <div data-testid="media-filter-bar" className="flex flex-wrap gap-2">
        <FilterPill label="All" active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
        {CATEGORIES.map((c) => (
          <FilterPill key={c.value} label={c.label} active={filter === c.value} onClick={() => setFilter(c.value)} testId={`filter-${c.value}`} />
        ))}
      </div>

      {/* Table */}
      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3">Downloads</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-white/40">
                  {items.length === 0 ? "No media assets yet. Click “New Media Asset” to add one." : "No assets in this category."}
                </td>
              </tr>
            )}
            {filtered.map((m) => {
              const Icon = catIcon(m.category);
              return (
                <tr key={m.id} data-testid={`media-row-${m.id}`} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3 text-white">
                    <div className="flex items-center gap-2.5">
                      <Icon size={14} className="text-aawta-pink shrink-0" />
                      <div className="min-w-0">
                        <p className="truncate">{m.title}</p>
                        {m.description && <p className="text-xs text-white/40 truncate">{m.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/70">{catLabel(m.category)}</td>
                  <td className="px-4 py-3 text-white/70">
                    {m.external_url ? (
                      <a href={m.external_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-aawta-pink hover:underline">
                        Link <ExternalLink size={11} />
                      </a>
                    ) : m.storage_path ? (
                      <span className="inline-flex items-center gap-1 text-white/70">
                        <Upload size={11} /> File
                      </span>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/50 font-mono text-xs">{formatSize(m.size)}</td>
                  <td className="px-4 py-3 text-white/80 font-mono text-xs">{m.download_count || 0}</td>
                  <td className="px-4 py-3">
                    {editable ? (
                      <button data-testid={`toggle-publish-${m.id}`} onClick={() => togglePublish(m)}
                              className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${m.published ? "border-aawta-pink/50 text-aawta-pink" : "border-white/20 text-white/50"}`}>
                        {m.published ? <Globe size={12} /> : <EyeOff size={12} />}
                        {m.published ? "PUBLISHED" : "DRAFT"}
                      </button>
                    ) : (
                      <span className="text-xs text-white/50">{m.published ? "Published" : "Draft"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editable && (
                      <>
                        <button data-testid={`edit-row-${m.id}`} onClick={() => setEditing({ ...m, _file: null })} className="text-white/60 hover:text-aawta-pink mr-3" title="Edit">
                          <Edit3 size={14} />
                        </button>
                        <button data-testid={`delete-row-${m.id}`} onClick={() => remove(m.id)} className="text-white/60 hover:text-red-400" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <MediaEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="p-5 border border-white/10 bg-aawta-charcoal/40">
      <p className="text-overline text-white/50">{label}</p>
      <p className="font-display text-3xl text-white mt-2">{value}</p>
    </div>
  );
}

function FilterPill({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-overline border transition-all ${
        active
          ? "border-aawta-pink text-aawta-pink bg-aawta-pink/10"
          : "border-white/15 text-white/60 hover:border-white/30 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function MediaEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.id && !file && !form.external_url) {
      toast.error("Upload a file or provide an external link");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        // PATCH metadata, then optionally POST a replacement file.
        await api.patch(`/admin/media/${form.id}`, {
          title: form.title, category: form.category, description: form.description || null,
          external_url: form.external_url || null,
          published: !!form.published, sort_order: Number(form.sort_order) || 0,
        }, { withCredentials: true });
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/admin/media/${form.id}/file`, fd, { withCredentials: true });
        }
        toast.success("Media updated");
      } else {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("category", form.category);
        if (form.description) fd.append("description", form.description);
        if (form.external_url) fd.append("external_url", form.external_url);
        fd.append("published", form.published ? "true" : "false");
        fd.append("sort_order", String(Number(form.sort_order) || 0));
        if (file) fd.append("file", file);
        await api.post("/admin/media", fd, { withCredentials: true });
        toast.success("Media created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={form.id ? "Edit Media Asset" : "New Media Asset"} onClose={onClose}>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <TextField label="Title *" value={form.title} onChange={(v) => update("title", v)} testId="media-title" />
        </div>
        <SelectField
          label="Category *"
          value={form.category}
          onChange={(v) => update("category", v)}
          options={CATEGORIES.map((c) => c.value)}
          testId="media-category"
        />
        <NumField label="Sort Order" value={form.sort_order} onChange={(v) => update("sort_order", v)} testId="media-sort-order" />

        <div className="md:col-span-2">
          <TextArea label="Description" value={form.description} onChange={(v) => update("description", v)} testId="media-description" />
        </div>

        <div className="md:col-span-2">
          <TextField
            label="External URL (optional — use instead of or alongside file)"
            value={form.external_url}
            onChange={(v) => update("external_url", v)}
            placeholder="https://..."
            testId="media-external-url"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block">
            <span className="text-overline text-white/50 mb-1.5 block">
              File {form.id ? "(upload to replace existing)" : "(optional if external URL provided)"}
            </span>
            <input
              type="file"
              data-testid="media-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full bg-aawta-jet/60 border border-white/10 text-white/80 px-3 py-2 text-sm file:mr-3 file:bg-aawta-pink/20 file:text-aawta-pink file:border-0 file:px-3 file:py-1.5 file:cursor-pointer"
            />
            {form.id && form.original_filename && !file && (
              <p className="text-xs text-white/40 mt-2">
                Current: {form.original_filename} ({formatSize(form.size)})
                {form.download_count ? ` · ${form.download_count} downloads` : ""}
              </p>
            )}
          </label>
        </div>

        <Toggle label="Published (visible on public site)" value={form.published} onChange={(v) => update("published", v)} testId="media-published" />

        {form.id && form.storage_path && (
          <a
            href={`/api/media/${form.id}/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost py-2 text-xs justify-self-start inline-flex items-center gap-2"
          >
            <Download size={13} /> PREVIEW DOWNLOAD
          </a>
        )}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button onClick={onClose} className="btn-ghost py-2.5">CANCEL</button>
        <button data-testid="save-media-btn" onClick={save} disabled={saving} className="btn-pink disabled:opacity-50">
          {saving ? "SAVING…" : "SAVE"}
        </button>
      </div>
    </Modal>
  );
}
